/**
 * Multi-Campaign Data Loader
 * 
 * Fetches and merges task data from multiple campaigns for Saved Views.
 * Replaces the stub data with real campaign data.
 */

import { layoutMultiCampaignTasks, calculateLayout } from '../SprintDetail/FlowchartView/layout';
import { taskMatchesFilters, loadCampaignsInBatches, CAMPAIGN_COLORS, getCampaignColor } from './utils';

// Re-export for backwards compatibility
export { CAMPAIGN_COLORS, getCampaignColor };

/**
 * Build campaigns with colors from loaded campaign data
 * @param {Object[]} loadedCampaigns - Loaded campaign objects
 * @returns {Object[]} Campaigns with color assignments
 */
function buildCampaignsWithColors(loadedCampaigns) {
  // Sort campaigns alphabetically by name for consistent ordering across refreshes
  const sortedCampaigns = [...loadedCampaigns].sort((a, b) => 
    (a.name || '').localeCompare(b.name || '')
  );

  // Build campaigns with colors (color assignment is now stable due to sorting)
  // Note: Don't set a fallback icon here - let the rendering components handle it
  // with the proper DefaultCampaignIcon SVG
  return sortedCampaigns.map((campaign, index) => ({
    id: campaign.id,
    name: campaign.name,
    icon: campaign.icon || null,
    color: getCampaignColor(index),
    statuses: campaign.statuses,
  }));
}

/**
 * Process tasks from a single campaign
 * @param {Object} campaignMeta - Campaign metadata with color
 * @param {Object[]} loadedCampaigns - All loaded campaigns
 * @param {Object} filters - View filters
 * @param {Set} pinnedTaskIds - Set of pinned task IDs
 * @returns {Object[]} Processed tasks with positions and source info
 */
function processCampaignTasks(campaignMeta, loadedCampaigns, filters, pinnedTaskIds) {
  const campaign = loadedCampaigns.find(c => c.id === campaignMeta.id);
  if (!campaign || !campaign.tasks) {
    console.log(`[MultiCampaignLoader] Campaign "${campaignMeta.name}": NO TASKS ARRAY (campaign found: ${!!campaign})`);
    return [];
  }

  const campaignTasks = campaign.tasks;
  const statuses = campaign.statuses || [];
  
  console.log(`[MultiCampaignLoader] Processing "${campaignMeta.name}": ${campaignTasks.length} raw tasks`);

  // Filter tasks - but KEEP archived tasks (Flow view shows them, other views filter client-side)
  let filteredOutCount = 0;
  const filteredTasks = campaignTasks.filter(task => {
    // Apply filters (pinned tasks bypass filters)
    // Note: We do NOT filter out archived tasks here - that's handled by the view layer
    // Flow view shows archived tasks (grayed out), other views may hide them
    if (!pinnedTaskIds.has(task.id) && !taskMatchesFilters(task, filters, statuses)) {
      filteredOutCount++;
      return false;
    }
    return true;
  });
  
  const archivedInResult = filteredTasks.filter(t => t.archived).length;
  console.log(`[MultiCampaignLoader] Campaign "${campaignMeta.name}": ${filteredTasks.length} tasks after filtering (${archivedInResult} archived, ${filteredOutCount} filtered out)`);

  // Use stored task.x/task.y positions - these are the user's manual arrangements
  // Only fall back to calculateLayout if positions are missing
  const tasksWithXY = filteredTasks.filter(t => t.x !== undefined && t.y !== undefined);
  
  let campaignPositions = {};
  if (tasksWithXY.length === filteredTasks.length && filteredTasks.length > 0) {
    // All tasks have stored positions - use them directly
    filteredTasks.forEach(task => {
      campaignPositions[task.id] = { x: task.x, y: task.y };
    });
    // Debug: Log loaded positions
    const sampleTask = filteredTasks[0];
    console.log(`[MultiCampaignLoader] Campaign "${campaignMeta.name}": loaded ${filteredTasks.length} tasks, sample "${sampleTask?.name?.slice(0,20)}": x=${sampleTask?.x}, y=${sampleTask?.y}`);
  } else if (filteredTasks.length > 0) {
    // Some tasks missing positions - calculate layout
    campaignPositions = calculateLayout(filteredTasks);
    console.log(`[MultiCampaignLoader] Campaign "${campaignMeta.name}": calculated layout for ${filteredTasks.length} tasks`);
  }

  // Add tasks with positions
  // Store the original intra-campaign position so we can correctly save back later
  return filteredTasks.map(task => {
    const position = campaignPositions[task.id] || { x: task.x ?? 100, y: task.y ?? 40 };
    return {
      ...task,
      position,
      // Store original intra-campaign position for correct save-back
      // This is the position BEFORE horizontal offsets are applied
      _originalPosition: { x: position.x, y: position.y },
      sourceCampaign: {
        id: campaignMeta.id,
        name: campaignMeta.name,
        icon: campaignMeta.icon,
        color: campaignMeta.color,
      },
    };
  });
}

/**
 * Apply multi-campaign layout and calculate offsets
 * @param {Object[]} rawTasks - Tasks with positions
 * @returns {{ tasks: Object[], offsets: Object }} Tasks with adjusted positions and campaign offsets
 */
function applyMultiCampaignLayout(rawTasks) {
  // Apply multi-campaign layout to ensure zones don't overlap
  // This adds horizontal offsets to separate campaign zones
  const adjustedPositions = layoutMultiCampaignTasks(rawTasks);

  // Calculate the offset applied to each campaign
  // We store this on each task so we can correctly reverse it when saving
  const campaignOffsets = {};
  rawTasks.forEach(task => {
    const campId = task.sourceCampaign?.id;
    if (campId && !campaignOffsets[campId]) {
      const original = task.position;
      const adjusted = adjustedPositions[task.id];
      if (original && adjusted) {
        campaignOffsets[campId] = adjusted.x - original.x;
      }
    }
  });
  
  // Debug log
  Object.entries(campaignOffsets).forEach(([campId, offset]) => {
    const sampleTask = rawTasks.find(t => t.sourceCampaign?.id === campId);
    console.log(`[MultiCampaignLoader] Campaign "${sampleTask?.sourceCampaign?.name}": offset=${offset}px`);
  });

  // Apply adjusted positions to tasks, and store the campaign offset for save-back
  const tasks = rawTasks.map(task => ({
    ...task,
    position: adjustedPositions[task.id] || task.position,
    // Store the exact offset that was applied to this campaign
    // This is used when saving to correctly reverse the offset
    _campaignOffset: campaignOffsets[task.sourceCampaign?.id] || 0,
  }));

  return { tasks, offsets: campaignOffsets };
}

/**
 * Deduplicate tasks by ID
 * @param {Object[]} tasks - Tasks to deduplicate
 * @returns {Object[]} Deduplicated tasks
 */
function deduplicateTasks(tasks) {
  const seenIds = new Set();
  return tasks.filter(task => {
    if (seenIds.has(task.id)) {
      console.warn(`[MultiCampaignLoader] Duplicate task ID detected and removed: ${task.id} (${task.name})`);
      return false;
    }
    seenIds.add(task.id);
    return true;
  });
}

/**
 * Log final results summary
 * @param {Object[]} tasks - Final task list
 * @param {Object[]} campaigns - Campaign list
 * @param {number} originalCount - Original task count before deduplication
 */
function logResultsSummary(tasks, campaigns, originalCount) {
  console.log('[MultiCampaignLoader] ═══════════════════════════════════════');
  console.log('[MultiCampaignLoader] FINAL RESULT:');
  console.log(`  Total tasks: ${tasks.length}${tasks.length !== originalCount ? ` (${originalCount - tasks.length} duplicates removed)` : ''}`);
  console.log(`  Total campaigns in legend: ${campaigns.length}`);
  
  // Group tasks by campaign to show distribution
  const tasksByCampaign = {};
  tasks.forEach(t => {
    const campName = t.sourceCampaign?.name || 'Unknown';
    tasksByCampaign[campName] = (tasksByCampaign[campName] || 0) + 1;
  });
  console.log('  Tasks per campaign:');
  Object.entries(tasksByCampaign).forEach(([name, count]) => {
    console.log(`    - "${name}": ${count} tasks`);
  });
  console.log('[MultiCampaignLoader] ═══════════════════════════════════════');
}

/**
 * Determine which campaign IDs to load based on scope
 * @param {string|string[]} scope - 'all' or array of campaign IDs
 * @param {Object[]} sprints - All available sprints
 * @returns {string[]|null} Campaign IDs to load, or null if invalid scope
 */
function determineCampaignIds(scope, sprints) {
  if (scope === 'all') {
    // All non-trashed, non-template, TOP-LEVEL campaigns only
    // (nested campaigns are children and shouldn't be loaded separately)
    const ids = sprints
      .filter(s => !s.deletedAt && !s.isTemplate && s.type !== 'template' && !s.parentSprintId)
      .map(s => s.id);
    console.log('[MultiCampaignLoader] Scope=all, found', ids.length, 'top-level campaigns');
    return ids;
  } else if (Array.isArray(scope)) {
    console.log('[MultiCampaignLoader] Scope=array with', scope.length, 'campaign IDs:', scope);
    return scope;
  } else {
    console.warn('[MultiCampaignLoader] Invalid scope:', scope);
    return null;
  }
}

/**
 * Load and merge data from multiple campaigns for a saved view
 * 
 * @param {Object} view - The SavedView configuration
 * @param {Object[]} sprints - All available sprints (may be metadata only)
 * @param {Function} loadFullSprint - Function to load full sprint data
 * @returns {Promise<{tasks: Object[], campaigns: Object[]} | null>}
 */
export async function loadMultiCampaignData(view, sprints, loadFullSprint) {
  if (!view || !view.query) {
    console.warn('[MultiCampaignLoader] No view or query provided');
    return null;
  }

  const { scope, filters, pinnedTasks = [] } = view.query;

  console.log('[MultiCampaignLoader] ═══════════════════════════════════════');
  console.log('[MultiCampaignLoader] View:', view.name, '(id:', view.id, ')');
  console.log('[MultiCampaignLoader] Scope:', scope);
  console.log('[MultiCampaignLoader] Available sprints:', sprints.length);

  // Determine which campaign IDs to load
  const campaignIds = determineCampaignIds(scope, sprints);
  if (!campaignIds) {
    return null;
  }

  if (campaignIds.length === 0) {
    console.warn('[MultiCampaignLoader] No campaigns to load');
    return { tasks: [], campaigns: [] };
  }

  console.log('[MultiCampaignLoader] Will load', campaignIds.length, 'campaigns');

  // Load campaigns with rate limiting
  const loadedCampaigns = await loadCampaignsInBatches(campaignIds, sprints, loadFullSprint);

  if (loadedCampaigns.length === 0) {
    console.warn('[MultiCampaignLoader] No campaigns loaded successfully');
    return { tasks: [], campaigns: [] };
  }
  
  console.log('[MultiCampaignLoader] Successfully loaded', loadedCampaigns.length, 'campaigns:');
  loadedCampaigns.forEach(c => {
    console.log(`  - "${c.name}" (id: ${c.id}, tasks: ${c.tasks?.length || 0})`);
  });

  // Build campaigns with colors
  const campaignsWithColors = buildCampaignsWithColors(loadedCampaigns);
  
  console.log('[MultiCampaignLoader] Campaigns for legend (with colors):');
  campaignsWithColors.forEach(c => {
    console.log(`  - "${c.name}" (color: ${c.color})`);
  });
  
  console.log('[MultiCampaignLoader] Filters being applied:', JSON.stringify(filters, null, 2));
  console.log('[MultiCampaignLoader] Pinned tasks:', pinnedTasks.length);

  // Build pinned task IDs set for quick lookup
  const pinnedTaskIds = new Set(pinnedTasks.map(pt => pt.taskId));

  // Merge tasks from all campaigns
  const rawTasks = [];
  campaignsWithColors.forEach(campaignMeta => {
    const tasks = processCampaignTasks(campaignMeta, loadedCampaigns, filters, pinnedTaskIds);
    rawTasks.push(...tasks);
  });

  // Apply multi-campaign layout
  const { tasks: layoutTasks } = applyMultiCampaignLayout(rawTasks);

  // Deduplicate tasks by ID (safety check)
  const deduplicatedTasks = deduplicateTasks(layoutTasks);

  // Final summary
  logResultsSummary(deduplicatedTasks, campaignsWithColors, layoutTasks.length);

  return {
    tasks: deduplicatedTasks,
    campaigns: campaignsWithColors,
  };
}

/**
 * Synchronous version that uses already-loaded sprint data
 * Use this when you know all campaigns are already fully loaded
 * 
 * @param {Object} view - The SavedView configuration
 * @param {Object[]} sprints - All available sprints (must have tasks loaded)
 * @returns {{tasks: Object[], campaigns: Object[]} | null}
 */
export function getMultiCampaignDataSync(view, sprints) {
  if (!view || !view.query) {
    return null;
  }

  const { scope, filters, pinnedTasks = [] } = view.query;

  // Determine which campaigns to include
  let campaigns;
  if (scope === 'all') {
    campaigns = sprints.filter(s => !s.deletedAt && !s.isTemplate && s.type !== 'template' && s.tasks);
  } else if (Array.isArray(scope)) {
    campaigns = sprints.filter(s => scope.includes(s.id) && s.tasks);
  } else {
    return null;
  }

  if (campaigns.length === 0) {
    return { tasks: [], campaigns: [] };
  }

  // Sort campaigns alphabetically by name for consistent ordering
  const sortedCampaigns = [...campaigns].sort((a, b) => 
    (a.name || '').localeCompare(b.name || '')
  );

  // Build campaigns with colors (color assignment is now stable due to sorting)
  // Note: Don't set a fallback icon here - let the rendering components handle it
  // with the proper DefaultCampaignIcon SVG
  const campaignsWithColors = sortedCampaigns.map((campaign, index) => ({
    id: campaign.id,
    name: campaign.name,
    icon: campaign.icon || null,
    color: getCampaignColor(index),
    statuses: campaign.statuses,
  }));

  // Build pinned task IDs set
  const pinnedTaskIds = new Set(pinnedTasks.map(pt => pt.taskId));

  // Merge tasks
  const rawTasks = [];
  
  campaignsWithColors.forEach(campaignMeta => {
    const campaign = sortedCampaigns.find(c => c.id === campaignMeta.id);
    if (!campaign || !campaign.tasks) return;

    campaign.tasks.forEach(task => {
      if (task.archived && !pinnedTaskIds.has(task.id)) return;
      if (!pinnedTaskIds.has(task.id) && !taskMatchesFilters(task, filters, campaign.statuses)) return;

      rawTasks.push({
        ...task,
        position: task.x !== undefined && task.y !== undefined 
          ? { x: task.x, y: task.y } 
          : undefined,
        sourceCampaign: {
          id: campaignMeta.id,
          name: campaignMeta.name,
          icon: campaignMeta.icon,
          color: campaignMeta.color,
        },
      });
    });
  });

  // Apply layout
  const adjustedPositions = layoutMultiCampaignTasks(rawTasks);
  const tasks = rawTasks.map(task => ({
    ...task,
    position: adjustedPositions[task.id] || task.position || { x: 0, y: 0 },
  }));

  return { tasks, campaigns: campaignsWithColors };
}
